import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MailService } from '../services/mail.service';
import { dropdownAnimation } from '@vex/animations/dropdown.animation';
import { fadeInUp400ms } from '@vex/animations/fade-in-up.animation';
import { VexLayoutService } from '@vex/services/vex-layout.service';
import { VexDateFormatRelativePipe } from '@vex/pipes/vex-date-format-relative/vex-date-format-relative.pipe';
import { MatRippleModule } from '@angular/material/core';
import { MailAttachmentComponent } from '../components/mail-attachment/mail-attachment.component';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { VexScrollbarComponent } from '@vex/components/vex-scrollbar/vex-scrollbar.component';
import { AsyncPipe, DatePipe, NgFor, NgIf } from '@angular/common';
import { MailRemarksDialogComponent } from '../components/mail-remarks-dialog/mail-remarks-dialog.component';
import { Mail } from '../interfaces/mail.interface';

@Component({
    selector: 'vex-mail-view',
    templateUrl: './mail-view.component.html',
    styleUrls: ['./mail-view.component.scss'],
    animations: [dropdownAnimation, fadeInUp400ms],
    imports: [
        NgIf,
        VexScrollbarComponent,
        MatButtonModule,
        MatTooltipModule,
        MatIconModule,
        NgFor,
        MatMenuModule,
        MailAttachmentComponent,
        MatRippleModule,
        AsyncPipe,
        DatePipe,
        VexDateFormatRelativePipe
    ]
})
export class MailViewComponent implements OnInit, OnDestroy {
  mail$ = this.mailService.selectedMail.asObservable();
  gtSm$ = this.layoutService.gtSm$;
  dropdownOpen = true;
  readonly currentUserId = this.mailService.getCurrentContext().userid;

  constructor(
    public mailService: MailService,
    private layoutService: VexLayoutService,
    private cd: ChangeDetectorRef,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {}

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
    this.cd.markForCheck();
  }

  reply() {
    const mail = this.mailService.selectedMail.value;
    if (mail) {
      this.mailService.requestCompose('reply', mail);
    }
  }

  replyAll() {
    const mail = this.mailService.selectedMail.value;
    if (mail) {
      this.mailService.requestCompose('replyAll', mail);
    }
  }

  forward() {
    const mail = this.mailService.selectedMail.value;
    if (mail) {
      this.mailService.requestCompose('forward', mail);
    }
  }

  printMail() {
    const mail = this.mailService.selectedMail.value;
    if (!mail) {
      return;
    }

    const printWindow = window.open('', '_blank', 'width=1024,height=768');
    if (!printWindow) {
      return;
    }

    const addresses = (mail.addresses || [])
      .map((item) => `<div><strong>${this.escapeHtml(item.type)}:</strong> ${this.escapeHtml(`${item.name || ''} ${item.email || ''}`.trim())}</div>`)
      .join('');

    const subject = this.escapeHtml(mail.subject || '(no subject)');
    const date = this.escapeHtml(new Date(mail.date).toLocaleString());
    const body = mail.body || '';

    printWindow.document.open();
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${subject}</title>
          <style>
            @page { margin: 24mm 16mm 18mm 16mm; }
            body { font-family: Arial, Helvetica, sans-serif; color: #1f2937; margin: 24px; }
            .mail-header { border-bottom: 1px solid #d1d5db; padding-bottom: 16px; margin-bottom: 24px; }
            .mail-subject { font-size: 24px; font-weight: 700; margin-bottom: 16px; }
            .mail-meta { font-size: 14px; line-height: 1.6; color: #374151; }
            .mail-body { font-size: 14px; line-height: 1.7; }
            .mail-body img, .mail-body table { max-width: 100% !important; height: auto !important; }
            .print-footer {
              position: fixed;
              left: 0;
              right: 0;
              bottom: 0;
              text-align: center;
              font-size: 12px;
              color: #6b7280;
              padding: 8px 0 0;
              border-top: 1px solid #e5e7eb;
              background: #ffffff;
            }
          </style>
        </head>
        <body>
          <div class="mail-header">
            <div class="mail-subject">${subject}</div>
            <div class="mail-meta">
              <div><strong>Date:</strong> ${date}</div>
              ${addresses}
            </div>
          </div>
          <div class="mail-body">${body}</div>
          <div class="print-footer">Powered by VCS Mail</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    window.setTimeout(() => {
      printWindow.print();
    }, 250);
  }

  deleteMail() {
    const mail = this.mailService.selectedMail.value;
    if (mail) {
      this.mailService.moveToFolder(mail.id, 'TRASH').subscribe();
    }
  }

  blockForMe() {
    const mail = this.mailService.selectedMail.value;
    if (!mail) {
      return;
    }

    this.dialog.open(MailRemarksDialogComponent, {
      width: '420px',
      data: {
        title: 'Block This Sender For Me',
        message: 'Future mails from this sender will not reach this user.',
        confirmText: 'Block for Me'
      }
    }).afterClosed().subscribe((remarks) => {
      if (remarks === undefined) {
        return;
      }

      this.mailService.blockMailForMe(mail.id, remarks).subscribe({
        next: () => {
          this.mailService.clearSelectedMail();
          this.mailService.loadMessages(1);
          this.mailService.loadFolderCounts();
        }
      });
    });
  }

  blockAsSuspicious() {
    const mail = this.mailService.selectedMail.value;
    if (!mail) {
      return;
    }

    this.dialog.open(MailRemarksDialogComponent, {
      width: '440px',
      data: {
        title: 'Block As Suspicious',
        message: 'Future mails from this sender will be blocked for everyone. Existing mails will show a suspicious warning.',
        confirmText: 'Block for All'
      }
    }).afterClosed().subscribe((remarks) => {
      if (remarks === undefined) {
        return;
      }

      this.mailService.blockMailAsSuspicious(mail.id, remarks).subscribe({
        next: () => {
          this.mailService.clearSelectedMail();
          this.mailService.loadMessages(1);
          this.mailService.loadFolderCounts();
        }
      });
    });
  }

  unblockSender() {
    const mail = this.mailService.selectedMail.value;
    if (!mail || !this.canUnblock(mail)) {
      return;
    }

    this.mailService.unblockSender(mail.id).subscribe({
      next: () => {
        this.mailService.clearSelectedMail();
        this.mailService.loadMessages(this.mailService.currentPage.value);
        this.mailService.loadFolderCounts();
      }
    });
  }

  canUnblock(mail: Mail | null | undefined) {
    return !!mail?.blockedInfo?.isBlocked && Number(mail.blockedInfo.blockedByUserId || 0) === this.currentUserId;
  }

  getBlockedHelpText(mail: Mail | null | undefined) {
    if (!mail?.blockedInfo?.isBlocked) {
      return '';
    }

    if (this.canUnblock(mail)) {
      if (mail.blockedInfo.scope === 'ALL') {
        return 'You blocked this sender for all users. You can unblock it if you want to allow messages from this sender again.';
      }

      return 'You blocked this sender for your mailbox. You can unblock it if you want to receive messages from this sender again.';
    }

    if (mail.blockedInfo.scope === 'ALL') {
      return 'This sender has been blocked for all users. Please contact your administrator if you believe this message should be unblocked.';
    }

    return 'This sender has been blocked for this mailbox.';
  }

  ngOnDestroy() {}

  private escapeHtml(value: string) {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
